/**
 * How far behind the newest sweep a mirrored calendar row may be and still be
 * shown.
 *
 * **Mostly historical since 23 Sep 2026, and kept as a backstop rather than a
 * mechanism.** The mirror was written by a scheduled Claude task whose DELETEs
 * the approval sandbox refused as often as it allowed, so rows were refreshed
 * in place and an event Zeena deleted could sit in the mirror for good — a
 * meeting on the grid that is not in the diary. The fix was to stamp
 * `mirrored_at` on every event a sweep saw and hide rows the latest sweep did
 * not touch.
 *
 * The mirror is now filled by the `mirror-calendar` edge function, which
 * replaces the whole window inside one transaction every fifteen minutes. A
 * deleted event is gone from the table, not merely stale, so nothing should
 * ever be filtered here. What remains is worth keeping for the case where the
 * replace stops happening while something else still writes a row — job C of
 * the calendar task adds its own new events to the mirror so they appear
 * before the next sweep.
 *
 * Ten hours, unchanged. It has to clear the longest legitimate gap between
 * whatever last wrote the table, and generosity is the safe direction: showing
 * an event that has gone is a smaller wrong than hiding one that is still on.
 *
 * Measured against the NEWEST row rather than the clock, which is the other
 * half of the safety. If the mirror stops entirely, every row is equally old,
 * nothing is hidden, and the calendar simply goes stale — which the page says
 * out loud. Compare against the clock instead and a stopped worker blanks the
 * calendar, which is the same information delivered as a lie.
 */
export const STALE_AFTER_MS = 10 * 60 * 60 * 1000

/**
 * Drops rows that the last couple of sweeps did not touch.
 *
 * A row with no readable stamp is kept, on the same principle as above.
 */
export function currentRows<T>(rows: T[], stampOf: (row: T) => number): T[] {
  const stamps = rows.map(stampOf).filter((value) => Number.isFinite(value))
  if (stamps.length === 0) return rows

  const newest = Math.max(...stamps)
  return rows.filter((row) => {
    const stamp = stampOf(row)
    return !Number.isFinite(stamp) || stamp >= newest - STALE_AFTER_MS
  })
}

/** The newest stamp across the rows, or null when none is readable. */
export function newestStamp<T>(rows: T[], stampOf: (row: T) => number): number | null {
  const stamps = rows.map(stampOf).filter((value) => Number.isFinite(value))
  return stamps.length > 0 ? Math.max(...stamps) : null
}
